import type { FontRef, ID, Story } from '@proofroom/schema';
import { resolveParaAttrs, resolveRunAttrs, type StyleLookup } from './attrs';
import type { SnapshotIndex } from './snapshotIndex';

/**
 * A face the draft declares missing is never a run override, so it is never a candidate for one
 * either: seeding the editor, materialising it back and composing all work from this list, and a
 * face mark for a face outside it would hand the missing font straight back as an override.
 */
export const isAvailableFace = (f: FontRef): boolean => f.status !== 'missing';

/** Every face the story's paragraphs and runs resolve to, after styles and overrides. */
export function storyFontIds(story: Story, lookup: StyleLookup): Set<ID> {
  const ids = new Set<ID>();
  for (const p of story.paragraphs) {
    const pa = resolveParaAttrs(p, lookup);
    if (pa.bullets?.fontId) ids.add(pa.bullets.fontId);
    if (!p.runs.length) ids.add(pa.fontId);
    for (const r of p.runs) ids.add(resolveRunAttrs(pa, r, lookup).fontId);
  }
  return ids;
}

export function unavailableFontsFor(story: Story, lookup: StyleLookup, loaded: Set<ID>): ID[] {
  return [...storyFontIds(story, lookup)].filter((id) => !loaded.has(id));
}

/** storyId to the faces it needs that the browser does not have; a story listed here is locked. */
export function fontLocks(idx: SnapshotIndex, loaded: Set<ID>): Record<ID, ID[]> {
  const lookup = { paraStyles: idx.paraStyles, charStyles: idx.charStyles, fonts: idx.fonts };
  const out: Record<ID, ID[]> = {};
  for (const story of Object.values(idx.stories)) {
    const missing = unavailableFontsFor(story, lookup, loaded);
    if (missing.length) out[story.id] = missing;
  }
  return out;
}
